import React, { useEffect, useState } from "react";
import { Button, Group, SegmentedControl, Stack, Text, Textarea } from "@mantine/core";
import type { CommentVisibility } from "./types";

/**
 * Inline composer for posting a new root comment or a reply.
 *
 * Visibility rules (mirrors backend ``create_comment``):
 *   - Tenant-portal users (``canPostAdmin=false``): always public,
 *     no selector.
 *   - Admin-only targets (``canPostPublic=false``, e.g. a support
 *     ticket): always admin, no selector. "Internal only" is a
 *     property of the target there, so there is no choice to offer.
 *   - Staff posting a root: full choice, defaults to admin.
 *   - Staff replying to an admin parent: locked to admin (no selector).
 *   - Staff replying to a public parent: full choice, defaults to admin
 *     (privacy-by-default — accidental "I'll just hit reply" stays
 *     staff-only until the staff member explicitly opts to make it
 *     tenant-visible).
 */

export interface CommentComposerProps {
  /** Override the composer's default visibility for *root* posts.
   *  Reply visibility is computed from the parent. */
  defaultVisibility?: CommentVisibility;
  canPostAdmin: boolean;
  /** False when the target admits no tenant-visible comments at all.
   *  Defaults to true so existing mounts are unchanged. */
  canPostPublic?: boolean;
  /** Replaces the locked-visibility caption. Use it to say what
   *  "internal" means on this target in the operator's own words. */
  lockedLabel?: string;
  /** Replying to this comment id; null/undefined for root posts.
   *  ``parentVisibility`` lets the composer pick the right rule
   *  (locked vs choice) without re-querying the server. */
  replyTo?: { id: number; authorName: string; parentVisibility: CommentVisibility } | null;
  onCancelReply?(): void;
  onSubmit(body: string, visibility: CommentVisibility): Promise<void> | void;
  /** Optional placeholder override. */
  placeholder?: string;
}

export const CommentComposer: React.FC<CommentComposerProps> = ({
  defaultVisibility,
  canPostAdmin,
  canPostPublic = true,
  lockedLabel,
  replyTo,
  onCancelReply,
  onSubmit,
  placeholder,
}) => {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  // Pick the initial visibility from rule:
  //   - tenant or admin-locked reply → "public"/"admin" with no choice
  //   - otherwise default to "admin" (or the explicit override)
  const visibilityLocked: CommentVisibility | null = (() => {
    if (!canPostAdmin) return "public";
    if (!canPostPublic) return "admin";
    if (replyTo && replyTo.parentVisibility === "admin") return "admin";
    return null;
  })();
  const initialVisibility: CommentVisibility =
    visibilityLocked ?? defaultVisibility ?? "admin";
  const [visibility, setVisibility] = useState<CommentVisibility>(initialVisibility);

  // Resync the choice whenever the lock state flips (e.g. user picks a
  // different reply target). Without this, the previous selection
  // sticks even when it's no longer valid.
  useEffect(() => {
    if (visibilityLocked) {
      setVisibility(visibilityLocked);
    } else {
      setVisibility(defaultVisibility ?? "admin");
    }
    // ``visibilityLocked`` and ``defaultVisibility`` are the only
    // inputs that matter — the user's current ``visibility`` choice
    // is intentionally not in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibilityLocked, defaultVisibility, replyTo?.id]);

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await onSubmit(trimmed, visibility);
      setBody("");
      onCancelReply?.();
    } finally {
      setBusy(false);
    }
  };

  const showSelector = !visibilityLocked;

  return (
    <Stack gap={6}>
      {replyTo && (
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            Replying to <strong>{replyTo.authorName || "comment"}</strong>
          </Text>
          {onCancelReply && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={onCancelReply}
              disabled={busy}
            >
              Cancel
            </Button>
          )}
        </Group>
      )}
      <Textarea
        autosize
        minRows={2}
        value={body}
        onChange={(e) => setBody(e.currentTarget.value)}
        placeholder={placeholder ?? (replyTo ? "Write a reply…" : "Add a comment…")}
        disabled={busy}
      />
      <Group justify="space-between">
        {showSelector ? (
          <SegmentedControl
            size="xs"
            data={[
              { value: "admin", label: "Staff only" },
              { value: "public", label: "Tenant-visible" },
            ]}
            value={visibility}
            onChange={(v) => setVisibility(v as CommentVisibility)}
          />
        ) : (
          <Text size="xs" c="dimmed">
            {lockedLabel ?? (visibilityLocked === "admin" ? "Staff only" : "Tenant-visible")}
          </Text>
        )}
        <Button
          size="xs"
          onClick={handleSubmit}
          loading={busy}
          disabled={!body.trim()}
        >
          Post
        </Button>
      </Group>
    </Stack>
  );
};
